/**
 * Leader Profile Bar
 *
 * Compact single-row summary for the leadership view — mirrors the shape of
 * ProviderProfileBar but scoped to a team. Shows manager name, clinician count,
 * context-aware key metrics, avg performance, patient counts, and projected awards.
 *
 * Care team is sourced from syn_employee_roster via manager_care_team on the
 * aggregation (authoritative HR source), not derived from provider list.
 *
 * Key metric logic:
 *   Admin view (no manager scoped — managerName is null):
 *     - "N / total passing ACO gate" — numerator AND denominator restricted to
 *       PCP Physician providers only (bonusEligibleType === 'PC Physician')
 *     - "N / total earning awards" — all providers where calculatedAward > 0
 *   Manager-scoped view (managerName is set):
 *     - PCP Physician team: ACO gate stat only
 *     - All other teams: earning awards stat only
 */

import React, { useState } from 'react'
import { motion, AnimatePresence } from 'motion/react'
import { Users, TrendingUp, UserCheck, ChevronRight } from 'lucide-react'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import { formatCurrency } from '@/lib/equip-utils'
import { useMeasureThresholdMap, type MeasureThresholdMap } from '@/hooks/equip/useMeasureThresholdMap'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import type { LeadershipAggregation } from '@/lib/equip-types'

interface LeaderProfileBarProps {
  aggregation: LeadershipAggregation | null
  isLoading: boolean
  isCurrentQuarter: boolean
  canSeeAwards?: boolean
  expandedContent?: React.ReactNode
}

/**
 * Resolve the operational ACO gate threshold (0-1) from the config schedule.
 * The gate is a pass/fail eligibility decision, so it uses the operational
 * threshold (Q1 0.69, Q2-Q4 0.89) to match the calculation engine — not the
 * communicated display value. ACO_Visits has a single provider type, so a
 * type-agnostic lookup is exact; falls back to the prior constants if the
 * config row hasn't loaded.
 */
function acoGateThreshold(thresholdMap: MeasureThresholdMap, quarter: number): number {
  const item = thresholdMap.items.find(
    (i) => i.isActive && i.measureName === 'ACO_Visits'
  )
  return item ? item.threshold : quarter === 1 ? 0.69 : 0.89
}

/** White-with-border tooltip style matching chart tooltips. */
const TOOLTIP_CLASS = 'bg-popover text-popover-foreground border border-border shadow-sm rounded-lg text-xs max-w-[240px]'

export function LeaderProfileBar({ aggregation, isLoading, isCurrentQuarter, canSeeAwards = true, expandedContent }: LeaderProfileBarProps) {
  const [isExpanded, setIsExpanded] = useState(false)
  const [isHovered, setIsHovered] = useState(false)

  // Called unconditionally (before the early returns) to satisfy the rules of
  // hooks; resolves to an empty map until the aggregation loads.
  const thresholdMap = useMeasureThresholdMap(aggregation?.year ?? 0, aggregation?.quarter ?? 0)

  if (isLoading) {
    return <Skeleton className="h-[68px] w-full rounded-[var(--radius)]" />
  }

  if (!aggregation) return null

  const handleToggle = () => {
    if (expandedContent) setIsExpanded((prev) => !prev)
  }

  const providers = aggregation.providers
  const activeProviders = providers.filter((p) => !p.isInactiveInQuarter)
  const totalProviders = providers.length
  const totalPatients = aggregation.totalPatients ?? 0
  const totalAcoPatients = aggregation.totalAcoPatients ?? 0
  const managerName = aggregation.managerName ?? null
  // Care team from syn_employee_roster (manager's own HR record), not derived from providers
  const careTeam = aggregation.managerCareTeam ?? null
  const managerIdentityUnresolved = aggregation.managerIdentityUnresolved ?? false
  const quarter = aggregation.quarter

  // --- Key metrics ---
  // Admin view: managerName is null → show both stats independently
  // Manager-scoped view: show only the stat relevant to the team's type

  const isAdminView = managerName === null

  // ACO gate stat — always restricted to active PCP Physician providers only
  const pcpPhysicians = activeProviders.filter((p) => p.bonusEligibleType === 'PC Physician')
  const acoThreshold = acoGateThreshold(thresholdMap, quarter)
  const acoPassingCount = pcpPhysicians.filter((p) => {
    const perf = p.measures['ACO_Visits']?.performance
    return perf !== null && perf !== undefined && perf >= acoThreshold
  }).length

  // Helper: returns true if a provider is eligible for this quarter.
  // Current quarter → eligibleAtQuarterStart; past quarter → eligibleAtQuarterEnd.
  const isProviderEligible = (p: (typeof providers)[number]): boolean =>
    isCurrentQuarter ? p.eligibleAtQuarterStart : p.eligibleAtQuarterEnd

  // Helper: returns true if a PCP Physician passes the ACO gate for this quarter.
  // Non-PCP-Physicians are not subject to the ACO gate and always return true.
  // Note: the DB does NOT zero out calculated_award for ACO-failing physicians —
  // the gate is a business rule enforced here in the UI.
  const passesAcoGate = (p: (typeof providers)[number]): boolean => {
    if (p.bonusEligibleType !== 'PC Physician') return true
    const perf = p.measures['ACO_Visits']?.performance
    return perf !== null && perf !== undefined && perf >= acoThreshold
  }

  // Award totals — only count active providers who are eligible AND pass the ACO gate
  const totalAwards = activeProviders.reduce(
    (sum, p) => sum + (isProviderEligible(p) && passesAcoGate(p) ? (p.calculatedAward ?? 0) : 0),
    0
  )
  const avgAward = activeProviders.length > 0 ? totalAwards / activeProviders.length : 0

  // Max possible award derivation:
  // calculated_award = totalPercentage * prorated_max_award (part-time adjusted).
  // Deriving max = award / totalPercentage gives each provider's individual max
  // (already prorated for part-time), which is the most accurate "ceiling" value.
  const totalMaxPossible = activeProviders
    .filter((p) => isProviderEligible(p) && passesAcoGate(p))
    .reduce((sum, p) => {
      if (p.totalPercentage && p.totalPercentage > 0 && p.calculatedAward != null) {
        return sum + p.calculatedAward / p.totalPercentage
      }
      return sum
    }, 0)

  // Earning awards stat — only count active eligible providers who pass the ACO gate
  const earningAwardsCount = activeProviders.filter(
    (p) => isProviderEligible(p) && (p.calculatedAward ?? 0) > 0 && passesAcoGate(p)
  ).length

  // For manager-scoped view, determine which single stat to show
  const isPcpPhysicianTeam = activeProviders.some((p) => p.bonusEligibleType === 'PC Physician')

  return (
    <div className="rounded-xl border border-border bg-background overflow-hidden">
      {/* Main bar row — clickable to expand/collapse when expandedContent is provided */}
      <div
        className={cn(
          'flex items-center justify-between gap-4 px-4 py-3',
          expandedContent && 'cursor-pointer select-none'
        )}
        onClick={handleToggle}
        onMouseEnter={() => expandedContent && setIsHovered(true)}
        onMouseLeave={() => expandedContent && setIsHovered(false)}
      >

        {/* Left: identity + metadata */}
        <div className="flex items-center gap-3 min-w-0">
          {/* Expand/collapse chevron — only rendered when expandedContent is provided */}
          {expandedContent && (
            <motion.div
              animate={{
                opacity: isHovered || isExpanded ? 1 : 0.3,
                rotate: isExpanded ? 90 : 0,
                scale: isHovered && !isExpanded ? 1.4 : 1,
              }}
              transition={{ duration: 0.2 }}
              className="shrink-0"
            >
              <ChevronRight className="h-4 w-4 text-muted-foreground/60" />
            </motion.div>
          )}

          {/* Avatar */}
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Users className="h-5 w-5 text-primary" />
          </div>

          <div className="min-w-0">
            {/* Title: manager name (when scoped) + care team badge */}
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-sm font-semibold text-foreground">
                {managerName ?? careTeam ?? (managerIdentityUnresolved ? (
                  <span
                    data-testid="leader-identity-unresolved"
                    className="text-warning"
                    title="This leader's identity could not be resolved to an employee number. Their team data may be incomplete."
                  >
                    Leader not identified
                  </span>
                ) : (
                  'Leadership View'
                ))}
              </h2>
              {careTeam && (
                <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold bg-primary/10 text-primary uppercase tracking-wider">
                  {careTeam}
                </span>
              )}
            </div>

            {/* Inline metadata row */}
            <div className="flex items-center gap-2 mt-1 flex-wrap text-sm">
              {/* Clinician count */}
              <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                <Users className="w-3.5 h-3.5" />
                <span className="font-semibold text-foreground">{totalProviders}</span>
                <span>{totalProviders === 1 ? 'clinician' : 'clinicians'}</span>
              </span>

              <span className="text-muted-foreground/40">|</span>

              {/* ACO gate stat — admin view always shows this; manager view shows only for PCP Physician teams */}
              {(isAdminView || isPcpPhysicianTeam) && (
                <>
                  <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                    <TrendingUp className="w-3.5 h-3.5" />
                    <span
                      className={cn(
                        'font-semibold',
                        acoPassingCount > 0
                          ? 'text-emerald-600 dark:text-emerald-400'
                          : 'text-muted-foreground'
                      )}
                    >
                      {acoPassingCount} / {pcpPhysicians.length}
                    </span>
                    <span>passing ACO gate</span>
                    {acoPassingCount > 0 && pcpPhysicians.length > 0 && (
                      <span className="text-muted-foreground/60 text-xs">
                        ({Math.round((acoPassingCount / pcpPhysicians.length) * 100)}%)
                      </span>
                    )}
                  </span>
                  <span className="text-muted-foreground/40">|</span>
                </>
              )}

              {/* Earning awards stat — hidden when canSeeAwards is false */}
              {canSeeAwards && (isAdminView || !isPcpPhysicianTeam) && (
                <>
                  <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                    <TrendingUp className="w-3.5 h-3.5" />
                    <span
                      className={cn(
                        'font-semibold',
                        earningAwardsCount > 0
                          ? 'text-emerald-600 dark:text-emerald-400'
                          : 'text-muted-foreground'
                      )}
                    >
                      {earningAwardsCount} / {totalProviders}
                    </span>
                    <span>EQuIP eligible</span>
                    {earningAwardsCount > 0 && (
                      <span className="text-muted-foreground/60 text-xs">
                        ({Math.round((earningAwardsCount / totalProviders) * 100)}%)
                      </span>
                    )}
                  </span>
                  <span className="text-muted-foreground/40">|</span>
                </>
              )}

              {/* Total patients */}
              <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                <UserCheck className="w-3.5 h-3.5" />
                <span className="font-semibold text-foreground">
                  {totalPatients.toLocaleString()}
                </span>
                <span>patients</span>
              </span>

              {totalAcoPatients > 0 && (
                <>
                  <span className="text-muted-foreground/40">|</span>

                  {/* ACO patients */}
                  <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                    <span className="font-semibold text-foreground">
                      {totalAcoPatients.toLocaleString()}
                    </span>
                    <span>ACO</span>
                  </span>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Right: financial summary — hidden when canSeeAwards is false */}
        {canSeeAwards && (
          <TooltipProvider>
            <div
              className="flex items-center gap-6 flex-shrink-0 text-right"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Total projected — with tooltip showing vs max possible */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="cursor-default">
                    <div className="text-sm font-bold text-foreground">{formatCurrency(totalAwards)}</div>
                    <p className="text-[11px] text-muted-foreground">total projected</p>
                  </div>
                </TooltipTrigger>
                <TooltipContent className={TOOLTIP_CLASS}>
                  <p className="font-medium mb-0.5">Total Projected Award</p>
                  <p className="text-muted-foreground">
                    {formatCurrency(totalAwards)} of {formatCurrency(totalMaxPossible)} total possible
                  </p>
                  <p className="text-muted-foreground mt-0.5">
                    across all eligible clinicians
                  </p>
                </TooltipContent>
              </Tooltip>

              {/* Avg / clinician */}
              <div className="hidden sm:block">
                <div className="text-sm font-bold text-foreground">{formatCurrency(avgAward)}</div>
                <p className="text-[11px] text-muted-foreground">avg / clinician</p>
              </div>
            </div>
          </TooltipProvider>
        )}
      </div>

      {/* Expandable content panel */}
      <AnimatePresence initial={false}>
        {isExpanded && expandedContent && (
          <motion.div
            key="leader-profile-expanded"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{
              height: { duration: 0.3, ease: [0.25, 0.1, 0.25, 1] },
              opacity: { duration: 0.2, delay: 0.05 },
            }}
            className="overflow-hidden border-t border-border"
          >
            <div className="p-4">
              {expandedContent}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
